import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Snackbar,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Layout from '../components/Layout';
import useIsMobile from '../hooks/useIsMobile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TelegramIcon from '@mui/icons-material/Telegram';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotesIcon from '@mui/icons-material/Notes';
import SchoolIcon from '@mui/icons-material/School';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonthlyReportTab, {
  MonthlyReportPreview,
} from '../components/MonthlyReportTab';
import BookMaterialsPicker, {
  pluralRu,
} from '../components/BookMaterialsPicker';

const MAX_TEMPLATE = 3000;

// Transliterate Cyrillic → Latin for safe filenames (Telegram + Windows).
const TRANSLIT_MAP = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};
function romanizeFilename(str, fallback = 'file') {
  if (!str) return fallback;
  let out = '';
  for (const ch of String(str)) {
    const lower = ch.toLowerCase();
    if (TRANSLIT_MAP[lower] !== undefined) {
      const r = TRANSLIT_MAP[lower];
      out += ch === lower ? r : r ? r[0].toUpperCase() + r.slice(1) : '';
    } else {
      out += ch;
    }
  }
  out = out
    .replace(/[^\w\s.-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return out || fallback;
}

// ── Helpers ───────────────────────────────────────────────────────────────
function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function getMonday(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}
function getSunday(dateStr) {
  const mon = new Date(getMonday(dateStr));
  mon.setDate(mon.getDate() + 6);
  return mon.toISOString().split('T')[0];
}
function todayStr() {
  return new Date().toISOString().split('T')[0];
}

const TYPE_LABEL = {
  word: 'Слово',
  phrase: 'Фраза',
  video: 'Видео',
  youtube: 'Видео', // backwards compat for old data
  craft: 'Активность',
  letter: 'Буква',
  number: 'Число',
  link: 'Ссылка',
};
const TYPE_COLOR = {
  word: '#C2233E',
  phrase: '#0891b2',
  video: '#dc2626',
  youtube: '#dc2626', // backwards compat
  craft: '#d97706',
  letter: '#16a34a',
  number: '#7c3aed',
  link: '#0369a1',
};
// Normalise legacy 'youtube' type to 'video' on read
function normaliseType(type) {
  return type === 'youtube' ? 'video' : type;
}

// ── Prompt template editor ────────────────────────────────────────────────
const HOMEWORK_PLACEHOLDERS = [
  { key: 'groupName', label: 'Название группы' },
  { key: 'teacherName', label: 'Учитель' },
  { key: 'date', label: 'Дата' },
  { key: 'studentCount', label: 'Кол-во учеников' },
  { key: 'studentNames', label: 'Имена учеников' },
  { key: 'words', label: 'Слова' },
  { key: 'phrases', label: 'Фразы' },
  { key: 'letters', label: 'Буквы' },
  { key: 'crafts', label: 'Активности' },
  { key: 'video', label: 'Видео ссылки (YouTube / VK / Rutube)' },
];

const WEEKLY_PLACEHOLDERS = [
  { key: 'teacherName', label: 'Учитель' },
  { key: 'weekStart', label: 'Начало недели' },
  { key: 'weekEnd', label: 'Конец недели' },
  { key: 'groupCount', label: 'Кол-во групп' },
  { key: 'groupsSummary', label: 'Сводка по группам' },
];

function PromptTemplateEditor({
  value,
  onChange,
  onSave,
  onReset,
  placeholders,
  saving,
  templateKey,
}) {
  const [open, setOpen] = useState(false);
  const textareaRef = useRef(null);
  const chars = value.length;
  const isOver = chars > MAX_TEMPLATE;

  const insertPlaceholder = (ph) => {
    const ta = textareaRef.current?.querySelector('textarea');
    const token = `{${ph}}`;
    if (!ta) {
      onChange((value + token).slice(0, MAX_TEMPLATE));
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = (value.slice(0, start) + token + value.slice(end)).slice(
      0,
      MAX_TEMPLATE
    );
    onChange(newVal);
    const newCursor = start + token.length;
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<TuneIcon fontSize="small" />}
        endIcon={
          open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )
        }
        onClick={() => setOpen((v) => !v)}
        sx={{
          borderRadius: 2,
          borderStyle: 'dashed',
          color: 'text.secondary',
          borderColor: 'grey.400',
        }}
      >
        {value.trim() ? 'Промпт-шаблон (активен)' : 'Настроить промпт-шаблон'}
        {value.trim() && (
          <Chip
            label="✓"
            size="small"
            color="success"
            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
          />
        )}
      </Button>

      <Collapse in={open}>
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'primary.200',
            bgcolor: 'primary.50',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Шаблон промпта
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, display: 'block' }}
          >
            Напишите свой шаблон. Вставляйте плейсхолдеры — они заменятся
            реальными данными при генерации. Если шаблон пустой, используется
            встроенный промпт.
          </Typography>

          {/* Placeholder chips */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
            >
              Кликните чтобы вставить в промпт:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {placeholders.map((p) => (
                <Chip
                  key={p.key}
                  label={`{${p.key}}`}
                  size="small"
                  onClick={() => insertPlaceholder(p.key)}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'primary.300',
                    color: 'primary.700',
                    '&:hover': { bgcolor: 'primary.100' },
                  }}
                  title={p.label}
                />
              ))}
            </Box>
          </Box>

          <TextField
            ref={textareaRef}
            multiline
            fullWidth
            minRows={6}
            maxRows={14}
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, MAX_TEMPLATE))}
            placeholder={`Напишите шаблон. Например:\n\nНапиши домашнее задание для группы {groupName}.\nУченики: {studentNames}\nСлова: {words}\nПодпиши от {teacherName}.`}
            variant="outlined"
            size="small"
            sx={{
              fontFamily: 'monospace',
              '& .MuiOutlinedInput-root': { bgcolor: 'white' },
            }}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isOver
                  ? 'error.main'
                  : chars > 2500
                    ? 'warning.main'
                    : 'text.disabled',
              }}
            >
              {chars} / {MAX_TEMPLATE} символов
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<RestartAltIcon fontSize="small" />}
                onClick={onReset}
                color="warning"
                disabled={!value.trim()}
              >
                Очистить
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )
                }
                onClick={onSave}
                disabled={saving || isOver}
              >
                Сохранить шаблон
              </Button>
            </Stack>
          </Stack>

          {value.trim() && (
            <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
              Шаблон активен — AI будет использовать ваш промпт вместо
              встроенного.
            </Alert>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
}

// ── Structured homework preview (same visual style as StudentProfilePage) ─
// Renders the weekly-summary plain-text content with nice typography:
// ALLCAPS lines → indigo section headers with underline.
// `• ` / `- ` → styled bullets.
// Everything else → paragraph.
function WeeklyPreview({ content }) {
  if (!content) return null;
  const INDIGO = '#1e3a8a';
  const INDIGO_MD = '#3b82f6';

  const paragraphs = String(content).split(/\n\s*\n/);
  const blocks = [];
  paragraphs.forEach((para, pi) => {
    const lines = para.split('\n');
    lines.forEach((raw, li) => {
      const line = raw.trim();
      if (!line) return;
      const upperNoColon = line.replace(/:$/, '').trim();
      const isHeader =
        upperNoColon.length > 0 &&
        upperNoColon.length < 60 &&
        upperNoColon === upperNoColon.toUpperCase() &&
        /[А-ЯЁA-Z]/.test(upperNoColon);
      const isBullet = /^[•\-*]\s/.test(line);
      // Labeled sub-line — "Слова: ...", "Фразы: ...", etc. Short Capitalized Russian word then colon.
      const labelMatch =
        !isHeader && !isBullet
          ? line.match(/^([А-ЯЁA-Z][а-яёa-zA-Z]{2,18}):\s+(.+)$/)
          : null;
      const key = `${pi}-${li}`;

      if (isHeader) {
        blocks.push(
          <Box key={key} sx={{ mt: blocks.length ? 3 : 0, mb: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: INDIGO,
                textTransform: 'uppercase',
              }}
            >
              {upperNoColon}
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                height: 2,
                width: 32,
                borderRadius: 1,
                bgcolor: INDIGO_MD,
              }}
            />
          </Box>
        );
      } else if (labelMatch) {
        const label = labelMatch[1];
        const body = labelMatch[2];
        blocks.push(
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.25,
              mb: 1,
              pl: 1.25,
              borderLeft: '3px solid',
              borderColor: INDIGO_MD,
              py: 0.25,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: INDIGO,
                flexShrink: 0,
                minWidth: 72,
              }}
            >
              {label}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontSize: 14,
                lineHeight: 1.7,
                color: 'text.primary',
              }}
            >
              {body}
            </Typography>
          </Box>
        );
      } else if (isBullet) {
        const body = line.replace(/^[•\-*]\s*/, '');
        blocks.push(
          <Stack
            key={key}
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ mb: 0.75 }}
          >
            <Box
              sx={{
                mt: '0.55em',
                width: 5,
                height: 5,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: INDIGO_MD,
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: 14, lineHeight: 1.7, color: 'text.primary' }}
            >
              {body}
            </Typography>
          </Stack>
        );
      } else {
        blocks.push(
          <Typography
            key={key}
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.75,
              color: 'text.primary',
              mb: 1,
            }}
          >
            {line}
          </Typography>
        );
      }
    });
  });
  return <Box>{blocks}</Box>;
}

// Null-safe readers for structured AI output — old saved items may lack
// fields or carry partial shapes; never let a bad field crash the preview.
function asText(v) {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}
function asList(v) {
  return Array.isArray(v) ? v.filter((x) => x != null) : [];
}
function wordParts(w) {
  if (typeof w === 'string') return { en: w, ru: '' };
  return { en: asText(w?.en), ru: asText(w?.ru) };
}

function StructuredPreview({ data }) {
  if (!data || typeof data !== 'object') return null;
  const RED = '#ef4444';
  const RED_LT = '#fef2f2';
  const RED_BD = '#fca5a5';
  const YELLOW = '#f59e0b';
  const YELLOW_LT = '#fffbeb';
  const YELLOW_BD = '#fde68a';
  const GREEN = '#16a34a';
  const GREEN_LT = '#f0fdf4';
  const TEAL = '#0891b2';
  const TEAL_LT = '#ecfeff';
  const INDIGO = '#1e3a8a';
  const THEME_COLORS = [
    '#C2233E',
    '#0891b2',
    '#d97706',
    '#16a34a',
    '#dc2626',
    '#7c3aed',
    '#0f766e',
  ];
  const themes = asList(data.themes).filter((t) => typeof t === 'object');
  const activities = asList(data.activities).filter(
    (a) => typeof a === 'object' && (asText(a.title) || asText(a.howTo))
  );
  const weekPlan = asList(data.weekPlan)
    .map((p) =>
      typeof p === 'string'
        ? { day: '', task: p }
        : { day: asText(p?.day), task: asText(p?.task) }
    )
    .filter((p) => p.day || p.task);
  const checklist = asList(data.parentChecklist).map(asText).filter(Boolean);
  const sectionLabel = (color) => ({
    display: 'block',
    color,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    mb: 0.75,
  });
  return (
    <Box sx={{ fontSize: '0.9rem' }}>
      {asText(data.greeting) && (
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {asText(data.greeting)}
        </Typography>
      )}
      {asText(data.intro) && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.7 }}
        >
          {asText(data.intro)}
        </Typography>
      )}
      {themes.map((theme, ti) => {
        const accent = THEME_COLORS[ti % THEME_COLORS.length];
        const learnWords = asList(theme.learnWords)
          .map(wordParts)
          .filter((w) => w.en);
        const reviewWords = asList(theme.reviewWords)
          .map(wordParts)
          .filter((w) => w.en);
        const hasLearn = learnWords.length > 0;
        const hasReview = reviewWords.length > 0;
        return (
          <Box
            key={ti}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {/* Theme header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: `${accent}14`,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 18,
                  borderRadius: 1,
                  bgcolor: accent,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{
                  color: accent,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  flexGrow: 1,
                }}
              >
                {asText(theme.name) || `Тема ${ti + 1}`}
              </Typography>
              {(hasLearn || hasReview) && (
                <Typography variant="caption" color="text.disabled">
                  {learnWords.length + reviewWords.length} шт.
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>
              {asText(theme.commentary) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, lineHeight: 1.65 }}
                >
                  {asText(theme.commentary)}
                </Typography>
              )}
              {/* Learn words */}
              {hasLearn && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: RED,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    Выучить:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {learnWords.map((w, wi) => (
                      <Box
                        key={wi}
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1.5,
                          bgcolor: RED_LT,
                          border: `1px solid ${RED_BD}`,
                          fontSize: '0.78rem',
                          color: '#7f1d1d',
                          fontWeight: 600,
                        }}
                      >
                        {w.en}
                        {w.ru ? (
                          <span style={{ color: '#b91c1c', fontWeight: 400 }}>
                            {' '}
                            ({w.ru})
                          </span>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                  {asText(theme.learnTips) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: '#fff7ed',
                        borderLeft: `3px solid ${RED}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#7c2d12', lineHeight: 1.6 }}
                      >
                        {asText(theme.learnTips)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {/* Review words */}
              {hasReview && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: YELLOW,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    Закрепить:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {reviewWords.map((w, wi) => (
                      <Box
                        key={wi}
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1.5,
                          bgcolor: YELLOW_LT,
                          border: `1px solid ${YELLOW_BD}`,
                          fontSize: '0.78rem',
                          color: '#78350f',
                          fontWeight: 600,
                        }}
                      >
                        {w.en}
                        {w.ru ? (
                          <span style={{ color: '#b45309', fontWeight: 400 }}>
                            {' '}
                            ({w.ru})
                          </span>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                  {asText(theme.reviewTips) && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: YELLOW_LT,
                        borderLeft: `3px solid ${YELLOW}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#78350f', lineHeight: 1.6 }}
                      >
                        {asText(theme.reviewTips)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
      {/* Home games */}
      {activities.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            fontWeight={800}
            sx={sectionLabel(TEAL)}
          >
            Игры дома
          </Typography>
          <Stack spacing={1}>
            {activities.map((a, ai) => {
              const minutes = Number(a.minutes);
              const words = asList(a.words)
                .map((w) => wordParts(w).en)
                .filter(Boolean);
              return (
                <Box
                  key={ai}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: TEAL_LT,
                    borderLeft: `3px solid ${TEAL}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ flexGrow: 1 }}
                    >
                      {asText(a.title) || `Игра ${ai + 1}`}
                    </Typography>
                    {minutes > 0 && (
                      <Chip
                        size="small"
                        label={`${minutes} мин`}
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: 'white',
                          color: TEAL,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                  {asText(a.howTo) && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, lineHeight: 1.6 }}
                    >
                      {asText(a.howTo)}
                    </Typography>
                  )}
                  {words.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.75,
                      }}
                    >
                      {words.map((w, wi) => (
                        <Chip
                          key={wi}
                          label={w}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: 'white',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
      {/* Week plan */}
      {weekPlan.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            fontWeight={800}
            sx={sectionLabel(INDIGO)}
          >
            План на неделю
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {weekPlan.map((p, pi) => (
              <Box
                key={pi}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderTop: pi ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                {p.day && (
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    sx={{ color: INDIGO, minWidth: 92, flexShrink: 0 }}
                  >
                    {p.day}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {p.task}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {/* End-of-week checklist for parents */}
      {checklist.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            fontWeight={800}
            sx={sectionLabel(GREEN)}
          >
            Проверьте в конце недели
          </Typography>
          <Stack spacing={0.5}>
            {checklist.map((c, ci) => (
              <Stack
                key={ci}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    mt: '4px',
                    width: 13,
                    height: 13,
                    flexShrink: 0,
                    borderRadius: 0.5,
                    border: `2px solid ${GREEN}`,
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {c}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
      {asText(data.praise) && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: GREEN_LT,
            borderLeft: `3px solid ${GREEN}`,
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: GREEN, display: 'block', mb: 0.25 }}
          >
            Молодец!
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#14532d', lineHeight: 1.65 }}
          >
            {asText(data.praise)}
          </Typography>
        </Box>
      )}
      {asText(data.closing) && (
        <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.7 }}>
          {asText(data.closing)}
        </Typography>
      )}
      {asText(data.signature) && (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          {asText(data.signature)}
        </Typography>
      )}
    </Box>
  );
}

// ── Shared JSON parser (same logic as StudentProfilePage) ────────────────
function tryParseStructured(text) {
  if (!text) return null;
  try {
    const p = JSON.parse(text.trim());
    if (p && p.themes) return p;
  } catch {}
  const stripped = text
    .replace(/```json\s*/gi, '')
    .replace(/```/g, '')
    .trim();
  try {
    const p = JSON.parse(stripped);
    if (p && p.themes) return p;
  } catch {}
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start !== -1 && end > start) {
    try {
      const p = JSON.parse(text.slice(start, end + 1));
      if (p && p.themes) return p;
    } catch {}
  }
  return null;
}

// Homework-shaped structure for preview/PDF. A manual edit makes the server
// drop structuredContent (edited: true) — then the teacher's text wins.
function homeworkStructure(item) {
  if (!item || item.edited) return null;
  const sc = item.structuredContent;
  if (sc) return Array.isArray(sc.themes) ? sc : null;
  return tryParseStructured(item.content);
}

// ── Content block (draft/sent item) ──────────────────────────────────────
function ContentBlock({ item, onSave, onReset, onSend, onDelete }) {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(item.content);
  const [editTitle, setEditTitle] = useState(item.title);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachPdf, setAttachPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const attachInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const structured = homeworkStructure(item);
  const monthlyStructure =
    item.type === 'monthly_report' && item.structuredContent?.report
      ? item.structuredContent
      : null;

  // Payload builders take the item explicitly: right after a save the
  // server response is newer than the `item` prop captured by this render.
  const buildPDFPayload = (src) => {
    const srcStructured = homeworkStructure(src);
    // Extract resources from either structured.resources OR contentSnapshot.items
    const snapshotItems = src.contentSnapshot?.items || [];
    const resourcesFromItems = snapshotItems
      .filter(
        (i) => i.type === 'video' || i.type === 'youtube' || i.type === 'link'
      )
      .filter((i) => (i.url || '').trim())
      .map((i) => ({
        title: i.text || i.url,
        url: i.url,
        type: i.type === 'link' ? 'link' : 'video',
        note: i.note || '',
      }));
    const resourcesFromStructured = Array.isArray(srcStructured?.resources)
      ? srcStructured.resources
      : [];
    const seen = new Set();
    const resources = [];
    for (const r of [...resourcesFromStructured, ...resourcesFromItems]) {
      if (!r?.url || seen.has(r.url)) continue;
      seen.add(r.url);
      resources.push(r);
    }

    return {
      title: src.title,
      content: src.content,
      language: 'ru',
      themeData: [],
      // null after a manual edit → the PDF is built from the edited text
      aiStructured: srcStructured || null,
      // Group fields
      groupName: src.groupName || null,
      groupStudentNames: src.groupStudentNames || null,
      groupScheduleText: src.groupScheduleText || null,
      teacherName: src.teacherDisplayName || null,
      bookLevel: src.contentSnapshot?.bookLevel || null,
      resources,
    };
  };

  const isWeekly = item.type === 'weekly_summary';

  const buildWeeklyPDFPayload = (src) => {
    const snap = src.contentSnapshot || {};
    const fmtShort = (iso) =>
      iso
        ? new Date(iso).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
          })
        : null;
    const fmtFull = (iso) =>
      iso
        ? new Date(iso).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : null;
    return {
      title: src.title,
      content: src.content,
      language: 'ru',
      teacherName: src.teacherDisplayName || null,
      weekStartLabel: fmtShort(snap.weekStartISO || src.weekStart),
      weekEndLabel: fmtFull(snap.weekEndISO || src.weekEnd),
      totalLessons: snap.totalLessons != null ? snap.totalLessons : null,
      totalStudents: snap.totalStudents != null ? snap.totalStudents : null,
      groupsSnapshot: Array.isArray(snap.groups) ? snap.groups : [],
    };
  };

  const fetchPDF = async (src) => {
    const req =
      src.type === 'monthly_report'
        ? {
            endpoint: '/api/pdf/monthly-report',
            payload: {
              ids: [src._id],
              lang: src.language === 'en' ? 'en' : 'ru',
            },
          }
        : src.type === 'weekly_summary'
          ? {
              endpoint: '/api/pdf/weekly-summary',
              payload: buildWeeklyPDFPayload(src),
            }
          : {
              endpoint: '/api/pdf/homework-sheet',
              payload: buildPDFPayload(src),
            };
    const res = await fetch(req.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(req.payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'PDF error');
    }
    return res.blob();
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const blob = await fetchPDF(item);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.title.replace(/[^\wа-яёА-ЯЁ 0-9_-]/gi, '').trim() || (isWeekly ? 'weekly' : 'homework')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
    } finally {
      setPdfLoading(false);
    }
  };

  const isDirty =
    editing && (editContent !== item.content || editTitle !== item.title);

  const handleConfirmSend = async () => {
    setSending(true);
    let current = item;
    // Unsaved edits are saved first, so Telegram and the PDF get the
    // teacher's text (and the server's structuredContent: null).
    if (isDirty) {
      let updated = null;
      setSaveError(null);
      try {
        updated = await onSave(item._id, {
          content: editContent,
          title: editTitle,
        });
      } catch (e) {
        setSaveError(e.message || 'Ошибка сети. Не удалось сохранить.');
      }
      if (!updated) {
        setSaveError(
          (prev) => prev || 'Не удалось сохранить. Отправка отменена.'
        );
        setSending(false);
        setSendOpen(false);
        return;
      }
      current = updated;
      setEditContent(updated.content);
      setEditTitle(updated.title);
    }
    if (editing) setEditing(false);
    let files = [...attachments];
    if (attachPdf) {
      try {
        const blob = await fetchPDF(current);
        const pdfFile = new File(
          [blob],
          romanizeFilename(current.title, 'homework') + '.pdf',
          {
            type: 'application/pdf',
          }
        );
        files = [pdfFile, ...files];
      } catch (e) {
        console.error('PDF generation for Telegram failed', e);
      }
    }
    await onSend(current._id, files);
    setSending(false);
    setSendOpen(false);
    setAttachments([]);
    setAttachPdf(false);
  };

  const isSent = item.status === 'sent';

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await onSave(item._id, {
        content: editContent,
        title: editTitle,
      });
      if (!updated) {
        setSaveError('Не удалось сохранить. Попробуйте ещё раз.');
        setSaving(false);
        return;
      }
      setEditContent(updated.content);
      setEditTitle(updated.title);
      setEditing(false);
    } catch (e) {
      setSaveError(e.message || 'Ошибка сети. Не удалось сохранить.');
    } finally {
      setSaving(false);
    }
  };

  // Keep editContent in sync with incoming item when NOT actively editing,
  // so external refreshes (reset, regenerate) don't get out of sync.
  useEffect(() => {
    if (!editing) {
      setEditContent(item.content);
      setEditTitle(item.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.content, item.title]);

  const handleCopy = () => {
    navigator.clipboard.writeText(item.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: isSent ? 'grey.200' : 'primary.100',
        borderRadius: 3,
        overflow: 'hidden',
        opacity: isSent ? 0.78 : 1,
        bgcolor: isSent ? 'grey.50' : 'white',
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {editing ? (
            <TextField
              size="small"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              fullWidth
              variant="standard"
              inputProps={{ style: { fontWeight: 700 } }}
            />
          ) : (
            <Typography
              variant="subtitle2"
              fontWeight={700}
              noWrap
              sx={{ color: isSent ? 'text.secondary' : 'text.primary' }}
            >
              {item.title}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
            {item.groupName && (
              <Chip
                size="small"
                icon={<GroupIcon sx={{ fontSize: 13 }} />}
                label={item.groupName}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            <Chip
              size="small"
              label={
                isSent ? `✓ Отправлено ${fmtDateTime(item.sentAt)}` : 'Черновик'
              }
              color={isSent ? 'success' : 'primary'}
              variant="filled"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {item.edited && (
              <Tooltip title="Текст изменён вручную — превью, PDF и Telegram используют вашу версию. «Сбросить» вернёт исходный текст AI.">
                <Chip
                  size="small"
                  label="Изменено вручную"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
            {isSent &&
              item.sentAttachments &&
              item.sentAttachments.length > 0 &&
              item.sentAttachments.map((att, i) => {
                const ext = att.name.includes('.')
                  ? att.name.split('.').pop().toUpperCase()
                  : '?';
                return (
                  <Chip
                    key={i}
                    size="small"
                    icon={
                      <AttachFileIcon sx={{ fontSize: '11px !important' }} />
                    }
                    label={
                      att.name.length > 22
                        ? `${att.name.slice(0, 19)}…${ext}`
                        : att.name
                    }
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', maxWidth: 200 }}
                  />
                );
              })}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ alignSelf: 'center' }}
            >
              {fmtDateTime(item.createdAt)}
            </Typography>
          </Stack>
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          {!editing && (
            <>
              <Tooltip title="Копировать">
                <IconButton size="small" onClick={handleCopy}>
                  {copied ? (
                    <CheckIcon fontSize="small" color="success" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Редактировать">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditing(true);
                    setExpanded(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Скачать PDF">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDownloadPDF}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
          {/* Stays available while editing: unsaved edits are saved first */}
          <Tooltip
            title={
              isDirty
                ? 'Сохранить правки и отправить в Telegram'
                : isSent
                  ? 'Отправить повторно'
                  : 'Отправить в Telegram'
            }
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => setSendOpen(true)}
            >
              <TelegramIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!editing && (
            <>
              <Tooltip title="Удалить">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteConfirm(true)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </Box>

      {/* Body */}
      {expanded && (
        <Box sx={{ px: 2.5, py: 2 }}>
          {editing ? (
            <>
              {saveError && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'error.50',
                    border: '1px solid',
                    borderColor: 'error.200',
                    color: 'error.dark',
                    fontSize: 13,
                  }}
                >
                  {saveError}
                </Box>
              )}
              <TextField
                multiline
                fullWidth
                minRows={item.type === 'weekly_summary' ? 18 : 10}
                maxRows={40}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                variant="outlined"
                placeholder="Текст отчёта..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    '& textarea': {
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: 'text.primary',
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                    },
                  },
                }}
              />
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  mt: 1.5,
                  pt: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {editContent.length.toLocaleString('ru-RU')} символов
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<RefreshIcon />}
                    onClick={async () => {
                      const updated = await onReset(item._id);
                      if (updated) {
                        setEditContent(updated.content);
                        setEditTitle(updated.title);
                      }
                      setEditing(false);
                    }}
                  >
                    Сбросить
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditing(false);
                      setEditContent(item.content);
                      setEditTitle(item.title);
                      setSaveError(null);
                    }}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CheckIcon />
                      )
                    }
                  >
                    Сохранить
                  </Button>
                </Stack>
              </Box>
            </>
          ) : monthlyStructure ? (
            <MonthlyReportPreview
              report={monthlyStructure.report}
              stats={monthlyStructure.stats}
              letter={item.content}
            />
          ) : structured ? (
            <StructuredPreview data={structured} />
          ) : item.type === 'weekly_summary' ? (
            <WeeklyPreview content={item.content} />
          ) : (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
            >
              {item.content}
            </Typography>
          )}
        </Box>
      )}

      <Dialog
        open={sendOpen}
        onClose={() => {
          if (!sending) {
            setSendOpen(false);
            setAttachments([]);
          }
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isSent ? 'Отправить повторно?' : 'Отправить в группу учителей?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSent
              ? 'Этот текст уже был отправлен. Хотите отправить снова?'
              : 'Текст будет отправлен в Telegram-группу учителей через бота Cherry-Pick Core.'}
          </Typography>
          {isDirty && (
            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
              Несохранённые правки будут сохранены перед отправкой.
            </Alert>
          )}

          {/* Attach PDF toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: attachPdf ? 'error.200' : 'grey.200',
              bgcolor: attachPdf ? 'error.50' : 'grey.50',
              cursor: 'pointer',
            }}
            onClick={() => setAttachPdf((v) => !v)}
          >
            <PictureAsPdfIcon
              sx={{
                color: attachPdf ? 'error.main' : 'text.disabled',
                fontSize: 20,
              }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Прикрепить PDF-файл
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Красиво отформатированный PDF будет сгенерирован и отправлен
                вместе с текстом
              </Typography>
            </Box>
            <Checkbox
              checked={attachPdf}
              onChange={(e) => {
                e.stopPropagation();
                setAttachPdf(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              color="error"
              size="small"
            />
          </Box>

          {/* Attachments */}
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => attachInputRef.current?.click()}
              disabled={attachments.length >= 10 || sending}
            >
              Прикрепить файлы
              {attachments.length > 0 ? ` (${attachments.length}/10)` : ''}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Фото, видео, документы · до 50 МБ · до 10 файлов
            </Typography>
            <input
              ref={attachInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              style={{ display: 'none' }}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                setAttachments((prev) => [...prev, ...picked].slice(0, 10));
                e.target.value = '';
              }}
            />
            {attachments.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {attachments.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      {(f.size / 1024 / 1024).toFixed(1)} МБ
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={sending}
                      onClick={() =>
                        setAttachments((prev) => prev.filter((_, j) => j !== i))
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {sending && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Отправка
                {attachments.length > 0
                  ? ` файлов (${attachments.length})`
                  : ''}
                ...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!sending) {
                setSendOpen(false);
                setAttachments([]);
              }
            }}
            disabled={sending}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            startIcon={
              sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            onClick={handleConfirmSend}
            disabled={sending}
          >
            {sending ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Удалить запись?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{item.title}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setDeleteConfirm(false);
              onDelete(item._id);
            }}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ── Group content preview ─────────────────────────────────────────────────
function GroupContentPreview({ items }) {
  if (!items || items.length === 0)
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        У этой группы нет материалов. Добавьте слова, фразы или ссылки в
        настройках группы на главной странице.
      </Alert>
    );
  const byType = {};
  items.forEach((item) => {
    if (!byType[item.type]) byType[item.type] = [];
    byType[item.type].push(item);
  });
  return (
    <Box sx={{ mt: 1.5 }}>
      {Object.entries(byType).map(([type, list]) => (
        <Box key={type} sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: TYPE_COLOR[type] || 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {TYPE_LABEL[type]} ({list.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {list.map((item, i) => (
              <Chip
                key={i}
                label={
                  item.type === 'youtube' ? item.text || item.url : item.text
                }
                size="small"
                sx={{
                  bgcolor: `${TYPE_COLOR[type]}18`,
                  color: TYPE_COLOR[type],
                  fontWeight: 600,
                  fontSize: '0.72rem',
                }}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Group Panel: metadata, theme progress, materials editor ──────────────
function GroupPanel({ group, groups, onRefresh }) {
  const [meta, setMeta] = useState({
    book: '',
    level: '',
    subject: '',
    notes: '',
  });
  const [themeProgress, setThemeProgress] = useState([]);
  const [content, setContent] = useState([]);
  const [newTheme, setNewTheme] = useState('');
  const [newItem, setNewItem] = useState({ type: 'word', text: '', url: '' });
  const [metaOpen, setMetaOpen] = useState(false);
  const [themesOpen, setThemesOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkPasteType, setBulkPasteType] = useState('word');
  const [copyToDialogOpen, setCopyToDialogOpen] = useState(false);
  const [copyToTargetId, setCopyToTargetId] = useState('');
  const [copyToReplace, setCopyToReplace] = useState(false);
  const [bookPickerOpen, setBookPickerOpen] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    setMeta({
      book: group.book || '',
      level: group.level || '',
      subject: group.subject || '',
      notes: group.notes || '',
    });
    setThemeProgress(group.themeProgress || []);
    // Normalise legacy 'youtube' type to 'video'
    setContent(
      (group.content || []).map((i) => ({ ...i, type: normaliseType(i.type) }))
    );
  }, [group._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchGroup = async (body) => {
    const res = await fetch(`/api/auth/groups/${group._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    onRefresh();
    return res.ok;
  };

  const saveMeta = async () => {
    setSaving(true);
    await patchGroup(meta);
    setSaving(false);
  };

  const saveThemes = async (updated) => {
    setThemeProgress(updated);
    await patchGroup({ themeProgress: updated });
  };

  const saveContent = async (updated) => {
    setContent(updated);
    const res = await fetch(`/api/auth/groups/${group._id}/content`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ content: updated }),
    });
    onRefresh();
    return res.ok;
  };

  const addTheme = async () => {
    const t = newTheme.trim();
    if (!t) return;
    const updated = [...themeProgress, { theme: t, lessonsCompleted: 0 }];
    setNewTheme('');
    await saveThemes(updated);
  };

  const removeTheme = async (i) => {
    await saveThemes(themeProgress.filter((_, j) => j !== i));
  };

  const adjustLessons = async (i, delta) => {
    const updated = themeProgress.map((tp, j) =>
      j === i
        ? {
            ...tp,
            lessonsCompleted: Math.min(
              8,
              Math.max(0, tp.lessonsCompleted + delta)
            ),
          }
        : tp
    );
    await saveThemes(updated);
  };

  const addItem = async () => {
    const text = newItem.text.trim();
    const url = newItem.url.trim();
    if (!text && !url) return;
    const updated = [...content, { type: newItem.type, text, url, note: '' }];
    setNewItem({ type: 'word', text: '', url: '' });
    await saveContent(updated);
  };

  const removeItem = async (i) => {
    await saveContent(content.filter((_, j) => j !== i));
  };

  const addBulkItems = async () => {
    const lines = bulkPasteText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const newItems = lines.map((text) => ({
      type: bulkPasteType,
      text,
      url: '',
      note: '',
    }));
    setBulkPasteText('');
    await saveContent([...content, ...newItems]);
    setSnack({
      open: true,
      message: `Добавлено ${newItems.length} элементов`,
      severity: 'success',
    });
  };

  // «Из книги»: the picker already skips items the group has; throwing keeps
  // the picker open with its error line.
  const addFromBook = async (items) => {
    const before = content;
    if (!(await saveContent([...before, ...items]))) {
      setContent(before);
      throw new Error('save failed');
    }
    setSnack({
      open: true,
      message: `Добавлено ${items.length} ${pluralRu(items.length, ['материал', 'материала', 'материалов'])}`,
      severity: 'success',
    });
  };

  // Book + its units as themes; themes the group already has keep their
  // lessonsCompleted, new units go to the end.
  const applyBookDetails = async (details) => {
    const known = new Set(
      themeProgress.map((tp) =>
        String(tp.theme || '')
          .trim()
          .toLowerCase()
      )
    );
    const added = [];
    (details.themeProgress || []).forEach((tp) => {
      const key = String(tp.theme || '')
        .trim()
        .toLowerCase();
      if (!key || known.has(key)) return;
      known.add(key);
      added.push(tp);
    });
    const merged = [...themeProgress, ...added];
    const book = details.book || meta.book;
    const level = details.level || meta.level;
    if (!(await patchGroup({ book, level, themeProgress: merged }))) {
      throw new Error('save failed');
    }
    setMeta((m) => ({ ...m, book, level }));
    setThemeProgress(merged);
    setThemesOpen(true);
    setSnack({
      open: true,
      message: added.length
        ? `Учебник «${book}», добавлено тем: ${added.length}`
        : `Учебник «${book}» установлен`,
      severity: 'success',
    });
  };

  const handleClone = async () => {
    const res = await fetch(`/api/auth/groups/${group._id}/clone`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      onRefresh();
      setSnack({
        open: true,
        message: 'Группа скопирована',
        severity: 'success',
      });
    }
  };

  const handleCopyTo = async () => {
    const res = await fetch(
      `/api/auth/groups/${group._id}/copy-content-to/${copyToTargetId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ replace: copyToReplace }),
      }
    );
    if (res.ok) {
      onRefresh();
      setCopyToDialogOpen(false);
      setSnack({
        open: true,
        message: copyToReplace ? 'Материалы заменены' : 'Материалы скопированы',
        severity: 'success',
      });
    }
  };

  const needsUrl =
    newItem.type === 'video' ||
    newItem.type === 'youtube' ||
    newItem.type === 'link';

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        overflow: 'hidden',
      }}
    >
      {/* Metadata section */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        onClick={() => setMetaOpen((v) => !v)}
      >
        <MenuBookIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
          Информация о группе
          {(meta.book || meta.level || meta.subject) && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {[meta.book, meta.level, meta.subject]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Typography>
        {metaOpen ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>
      <Collapse in={metaOpen}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                label="Учебник"
                fullWidth
                value={meta.book}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, book: e.target.value }))
                }
                InputProps={{
                  startAdornment: (
                    <MenuBookIcon
                      sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }}
                    />
                  ),
                }}
              />
              <TextField
                size="small"
                label="Уровень (A1, B2...)"
                value={meta.level}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, level: e.target.value }))
                }
                sx={{ minWidth: 150 }}
                InputProps={{
                  startAdornment: (
                    <SchoolIcon
                      sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }}
                    />
                  ),
                }}
              />
              <TextField
                size="small"
                label="Предмет"
                value={meta.subject}
                onChange={(e) =>
                  setMeta((m) => ({ ...m, subject: e.target.value }))
                }
                sx={{ minWidth: 150 }}
              />
            </Stack>
            <TextField
              size="small"
              label="Заметки"
              fullWidth
              multiline
              rows={2}
              value={meta.notes}
              onChange={(e) =>
                setMeta((m) => ({ ...m, notes: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <NotesIcon
                    sx={{
                      mr: 1,
                      mt: 0.5,
                      fontSize: 18,
                      color: 'text.secondary',
                      alignSelf: 'flex-start',
                    }}
                  />
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={saveMeta}
                disabled={saving}
              >
                Сохранить
              </Button>
            </Box>
          </Stack>
        </Box>
      </Collapse>

      {/* Theme progress section */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        onClick={() => setThemesOpen((v) => !v)}
      >
        <SchoolIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
          Прогресс по темам ({themeProgress.length})
        </Typography>
        {themesOpen ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>
      <Collapse in={themesOpen}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {themeProgress.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexGrow: 1 }}
                >
                  Тем пока нет — можно взять юниты из учебника
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setBookPickerOpen(true)}
                >
                  📚 Из книги
                </Button>
              </Box>
            )}
            {themeProgress.map((tp, i) => (
              <Box key={i}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flexGrow: 1 }}
                  >
                    {tp.theme}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 40, textAlign: 'right' }}
                  >
                    {tp.lessonsCompleted}/8
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => adjustLessons(i, -1)}
                    disabled={tp.lessonsCompleted === 0}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => adjustLessons(i, 1)}
                    disabled={tp.lessonsCompleted === 8}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeTheme(i)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(tp.lessonsCompleted / 8) * 100}
                  sx={{ borderRadius: 1, height: 6 }}
                  color={tp.lessonsCompleted === 8 ? 'success' : 'primary'}
                />
              </Box>
            ))}
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                placeholder="Новая тема..."
                value={newTheme}
                onChange={(e) => setNewTheme(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTheme()}
                sx={{ flexGrow: 1 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addTheme}
                disabled={!newTheme.trim()}
              >
                Добавить
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {/* Materials section */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          bgcolor: 'grey.50',
        }}
        onClick={() => setMaterialsOpen((v) => !v)}
      >
        <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
          Материалы ({content.length})
        </Typography>
        {materialsOpen ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>
      <Collapse in={materialsOpen}>
        <Box sx={{ p: 2 }}>
          {/* Toolbar */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1.5 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => setBookPickerOpen(true)}
            >
              📚 Из книги
            </Button>
            <Button
              size="small"
              variant={showBulkPaste ? 'contained' : 'outlined'}
              onClick={() => setShowBulkPaste((v) => !v)}
            >
              Массово
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={content.length === 0}
              onClick={() => {
                const blob = new Blob([JSON.stringify(content, null, 2)], {
                  type: 'application/json',
                });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `materials-${group.name || 'group'}.json`;
                a.click();
              }}
            >
              Экспорт
            </Button>
            <Button size="small" variant="outlined" component="label">
              Импорт
              <input
                type="file"
                accept=".json"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async (ev) => {
                    try {
                      const imported = JSON.parse(ev.target.result);
                      if (Array.isArray(imported)) {
                        await saveContent([...content, ...imported]);
                        setSnack({
                          open: true,
                          message: `Импортировано ${imported.length} элементов`,
                          severity: 'success',
                        });
                      }
                    } catch {
                      setSnack({
                        open: true,
                        message: 'Ошибка импорта JSON',
                        severity: 'error',
                      });
                    }
                  };
                  reader.readAsText(file);
                  e.target.value = '';
                }}
              />
            </Button>
            <Button size="small" variant="outlined" onClick={handleClone}>
              Клонировать группу
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={content.length === 0}
              onClick={() => {
                setCopyToTargetId('');
                setCopyToReplace(false);
                setCopyToDialogOpen(true);
              }}
            >
              Копировать материалы в...
            </Button>
          </Stack>

          {/* Bulk paste */}
          {showBulkPaste && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'primary.200',
                borderRadius: 2,
                bgcolor: 'primary.50',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Select
                  size="small"
                  value={bulkPasteType}
                  onChange={(e) => setBulkPasteType(e.target.value)}
                  sx={{ minWidth: 110 }}
                >
                  {Object.keys(TYPE_LABEL).map((t) => (
                    <MenuItem key={t} value={t}>
                      {TYPE_LABEL[t]}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexGrow: 1 }}
                >
                  {
                    'Вставьте несколько записей (через новую строку или запятую)'
                  }
                </Typography>
              </Stack>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder={'apple, banana, cat\ndog\nelephant'}
                value={bulkPasteText}
                onChange={(e) => setBulkPasteText(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!bulkPasteText.trim()}
                onClick={addBulkItems}
              >
                {'Добавить '}
                {
                  bulkPasteText.split(/[\n,]/).filter((s) => s.trim()).length
                }{' '}
                {' элементов'}
              </Button>
            </Box>
          )}

          {/* Chips list */}
          {content.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {content.map((item, i) => (
                <Chip
                  key={i}
                  label={
                    needsUrl && item.type === newItem.type
                      ? item.text || item.url
                      : item.text || item.url
                  }
                  size="small"
                  onDelete={() => removeItem(i)}
                  sx={{
                    bgcolor: `${TYPE_COLOR[item.type] || '#888'}18`,
                    color: TYPE_COLOR[item.type] || 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Single item add row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Select
              size="small"
              value={newItem.type}
              onChange={(e) =>
                setNewItem((p) => ({ ...p, type: e.target.value, url: '' }))
              }
              sx={{ minWidth: 110 }}
            >
              {Object.keys(TYPE_LABEL).map((t) => (
                <MenuItem key={t} value={t}>
                  {TYPE_LABEL[t]}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder={
                needsUrl ? 'YouTube / VK Video / Rutube URL' : 'Текст...'
              }
              value={needsUrl ? newItem.url : newItem.text}
              onChange={(e) =>
                setNewItem((p) =>
                  needsUrl
                    ? { ...p, url: e.target.value }
                    : { ...p, text: e.target.value }
                )
              }
              onKeyDown={(e) => e.key === 'Enter' && addItem()}
              sx={{ flexGrow: 1 }}
            />
            {needsUrl && (
              <TextField
                size="small"
                placeholder="Название (опц.)"
                value={newItem.text}
                onChange={(e) =>
                  setNewItem((p) => ({ ...p, text: e.target.value }))
                }
                onKeyDown={(e) => e.key === 'Enter' && addItem()}
                sx={{ flexGrow: 1 }}
              />
            )}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addItem}
              disabled={needsUrl ? !newItem.url.trim() : !newItem.text.trim()}
            >
              Добавить
            </Button>
          </Stack>
        </Box>
      </Collapse>

      <BookMaterialsPicker
        open={bookPickerOpen}
        onClose={() => setBookPickerOpen(false)}
        existingItems={content}
        onAdd={addFromBook}
        onApplyDetails={applyBookDetails}
      />

      {/* Copy-to dialog */}
      <Dialog
        open={copyToDialogOpen}
        onClose={() => setCopyToDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Копировать материалы в группу
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Целевая группа</InputLabel>
            <Select
              value={copyToTargetId}
              label="Целевая группа"
              onChange={(e) => setCopyToTargetId(e.target.value)}
            >
              {(groups || [])
                .filter((g) => g._id !== group._id)
                .map((g) => (
                  <MenuItem key={g._id} value={g._id}>
                    {g.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Checkbox
                checked={copyToReplace}
                onChange={(e) => setCopyToReplace(e.target.checked)}
              />
            }
            label="Заменить существующие материалы (иначе добавить)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyToDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            disabled={!copyToTargetId}
            onClick={handleCopyTo}
          >
            Копировать
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

// ── Shared content-block callbacks (reused in all tabs) ───────────────────
function makeContentCallbacks(setResult, onGenerated) {
  return {
    onSave: async (id, updates) => {
      const r = await fetch(`/api/ai/content/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Не удалось сохранить.');
      }
      const updated = await r.json();
      // Refresh parent's view state so downstream consumers (PDF export,
      // Telegram send, preview) see the saved version — not the pre-edit one.
      setResult(updated);
      onGenerated();
      return updated;
    },
    onReset: async (id) => {
      const r = await fetch(`/api/ai/content/${id}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return null;
      const updated = await r.json();
      setResult(updated);
      onGenerated();
      return updated;
    },
    onSend: async (id, files = []) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const r = await fetch(`/api/ai/content/${id}/send`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (r.ok) setResult(null);
      onGenerated();
      return r.ok;
    },
    onDelete: async (id) => {
      await fetch(`/api/ai/content/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setResult(null);
      onGenerated();
    },
  };
}

// ── Homework Tab ──────────────────────────────────────────────────────────
function HomeworkTab({
  groups,
  onGenerated,
  onRefreshGroups,
  onSendFeedback,
  template,
  onSaveTemplate,
}) {
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [localTemplate, setLocalTemplate] = useState(template);
  const [savingTpl, setSavingTpl] = useState(false);

  useEffect(() => {
    setLocalTemplate(template);
  }, [template]);

  const selectedGroup = groups.find((g) => g._id === selectedGroupId);
  const baseCbs = makeContentCallbacks(setResult, onGenerated);
  const cbs = {
    ...baseCbs,
    onSend: async (id, files) => {
      const ok = await baseCbs.onSend(id, files);
      onSendFeedback?.(ok);
    },
  };

  const handleSaveTemplate = async () => {
    setSavingTpl(true);
    await onSaveTemplate('homework', localTemplate);
    setSavingTpl(false);
  };

  const handleGenerate = async () => {
    if (!selectedGroupId) return;
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('/api/ai/generate/homework', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ groupId: selectedGroupId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Generation failed');
      setResult(data);
      onGenerated();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Выберите группу — AI напишет письмо с домашним заданием для родителей на
        основе материалов группы.
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Выберите группу</InputLabel>
        <Select
          value={selectedGroupId}
          label="Выберите группу"
          onChange={(e) => {
            setSelectedGroupId(e.target.value);
            setResult(null);
            setError('');
          }}
          startAdornment={
            <GroupIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
          }
        >
          {groups.map((g) => (
            <MenuItem key={g._id} value={g._id}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {g.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {g.studentIds?.length || 0} учеников •{' '}
                  {g.content?.length || 0} материалов
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedGroup && (
        <GroupPanel
          group={selectedGroup}
          groups={groups}
          onRefresh={onRefreshGroups}
        />
      )}

      <PromptTemplateEditor
        value={localTemplate}
        onChange={setLocalTemplate}
        onSave={handleSaveTemplate}
        onReset={() => setLocalTemplate('')}
        placeholders={HOMEWORK_PLACEHOLDERS}
        saving={savingTpl}
        templateKey="homework"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        disabled={
          !selectedGroupId || generating || !selectedGroup?.content?.length
        }
        onClick={handleGenerate}
        startIcon={
          generating ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoAwesomeIcon />
          )
        }
        sx={{ mb: 3 }}
      >
        {generating ? 'Генерирую...' : 'Сгенерировать домашнее задание'}
      </Button>

      {result && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Результат:
          </Typography>
          <ContentBlock item={result} {...cbs} />
        </Box>
      )}
    </Box>
  );
}

// ── Weekly Summary Tab ────────────────────────────────────────────────────
function WeeklyTab({
  groups,
  onGenerated,
  onSendFeedback,
  template,
  onSaveTemplate,
}) {
  const [baseDate, setBaseDate] = useState(todayStr());
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [localTemplate, setLocalTemplate] = useState(template);
  const [savingTpl, setSavingTpl] = useState(false);

  useEffect(() => {
    setLocalTemplate(template);
  }, [template]);

  const weekStart = getMonday(baseDate);
  const weekEnd = getSunday(baseDate);
  const baseCbs = makeContentCallbacks(setResult, onGenerated);
  const cbs = {
    ...baseCbs,
    onSend: async (id, files) => {
      const ok = await baseCbs.onSend(id, files);
      onSendFeedback?.(ok);
    },
  };

  const toggleGroup = (id) =>
    setSelectedGroups((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );

  const handleSaveTemplate = async () => {
    setSavingTpl(true);
    await onSaveTemplate('weeklySummary', localTemplate);
    setSavingTpl(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch('/api/ai/generate/weekly-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          weekStart,
          weekEnd,
          groupIds: selectedGroups.length > 0 ? selectedGroups : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Generation failed');
      setResult(data);
      onGenerated();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const fmtShort = (d) =>
    new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AI составит еженедельный отчёт на основе материалов, заданных для групп.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Любая дата из нужной недели
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={baseDate}
            onChange={(e) => {
              setBaseDate(e.target.value);
              setResult(null);
            }}
            InputProps={{
              startAdornment: (
                <CalendarMonthIcon
                  sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }}
                />
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <Typography variant="body2" fontWeight={700} color="primary.main">
              {fmtShort(weekStart)} — {fmtShort(weekEnd)}
            </Typography>
          </Paper>
        </Box>
      </Stack>

      {groups.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Фильтр групп (по умолчанию — все)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {groups.map((g) => (
              <Chip
                key={g._id}
                label={`${g.name} (${g.content?.length || 0})`}
                size="small"
                onClick={() => toggleGroup(g._id)}
                color={selectedGroups.includes(g._id) ? 'primary' : 'default'}
                variant={selectedGroups.includes(g._id) ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>
      )}

      <PromptTemplateEditor
        value={localTemplate}
        onChange={setLocalTemplate}
        onSave={handleSaveTemplate}
        onReset={() => setLocalTemplate('')}
        placeholders={WEEKLY_PLACEHOLDERS}
        saving={savingTpl}
        templateKey="weeklySummary"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        disabled={generating || groups.length === 0}
        onClick={handleGenerate}
        startIcon={
          generating ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoAwesomeIcon />
          )
        }
        sx={{ mb: 3 }}
      >
        {generating ? 'Генерирую...' : 'Сгенерировать отчёт за неделю'}
      </Button>

      {result && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Результат:
          </Typography>
          <ContentBlock item={result} {...cbs} />
        </Box>
      )}
    </Box>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────
function HistoryTab({ refreshKey, onSendFeedback }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/ai/content', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const displayed =
    filter === 'all' ? items : items.filter((i) => i.status === filter);

  const handleSave = async (id, updates) => {
    const r = await fetch(`/api/ai/content/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.message || 'Не удалось сохранить.');
    }
    const updated = await r.json();
    setItems((prev) => prev.map((i) => (i._id === id ? updated : i)));
    return updated;
  };

  const handleReset = async (id) => {
    const r = await fetch(`/api/ai/content/${id}/reset`, {
      method: 'POST',
      credentials: 'include',
    });
    if (r.ok) {
      const updated = await r.json();
      setItems((prev) => prev.map((i) => (i._id === id ? updated : i)));
      return updated;
    }
    return null;
  };

  const handleSend = async (id, files = []) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const r = await fetch(`/api/ai/content/${id}/send`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (r.ok) {
      const updated = await r.json();
      setItems((prev) => prev.map((i) => (i._id === id ? updated : i)));
      onSendFeedback?.(true);
    } else {
      onSendFeedback?.(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`/api/ai/content/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setItems((prev) => prev.filter((i) => i._id !== id));
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {['all', 'draft', 'sent'].map((f) => (
          <Chip
            key={f}
            label={
              f === 'all' ? 'Все' : f === 'draft' ? 'Черновики' : 'Отправленные'
            }
            size="small"
            onClick={() => setFilter(f)}
            color={filter === f ? 'primary' : 'default'}
            variant={filter === f ? 'filled' : 'outlined'}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={load}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>
      {loading ? (
        <Typography color="text.secondary">Загрузка...</Typography>
      ) : displayed.length === 0 ? (
        <Alert severity="info">Нет сохранённых материалов.</Alert>
      ) : (
        displayed.map((item) => (
          <ContentBlock
            key={item._id}
            item={item}
            onSave={handleSave}
            onReset={handleReset}
            onSend={handleSend}
            onDelete={handleDelete}
          />
        ))
      )}
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function AIPage() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState(0);
  const [groups, setGroups] = useState([]);
  const [historyKey, setHistoryKey] = useState(0);
  const [templates, setTemplates] = useState({
    homework: '',
    weeklySummary: '',
  });
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadGroups = useCallback(() => {
    fetch('/api/auth/groups', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setGroups(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadGroups();
    fetch('/api/ai/templates', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setTemplates(d || { homework: '', weeklySummary: '' }))
      .catch(() => {});
  }, [loadGroups]);

  const handleSaveTemplate = async (key, value) => {
    const res = await fetch('/api/ai/templates', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ [key]: value }),
    });
    if (res.ok) {
      const updated = await res.json();
      setTemplates(updated);
    }
  };

  const refresh = () => setHistoryKey((k) => k + 1);

  return (
    <Layout title="AI Ассистент">
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 0.5 }}
        >
          <AutoAwesomeIcon sx={{ color: '#E8A93D', fontSize: 32 }} />
          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              background: 'linear-gradient(135deg, #E8A93D, #C2233E)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI Ассистент
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Генерация домашних заданий, недельных и месячных отчётов на основе
          материалов групп • Отправка в Telegram
        </Typography>
      </Box>

      {groups.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          У вас нет групп. Создайте группы на главной странице и добавьте в них
          материалы — тогда AI сможет генерировать контент.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<AssignmentIcon fontSize="small" />}
            iconPosition="start"
            label="Домашнее задание"
          />
          <Tab
            icon={<CalendarMonthIcon fontSize="small" />}
            iconPosition="start"
            label="Итог недели"
          />
          <Tab
            icon={<AssessmentIcon fontSize="small" />}
            iconPosition="start"
            label="Месячный отчёт"
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label="История"
          />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {tab === 0 && (
            <HomeworkTab
              groups={groups}
              onGenerated={refresh}
              onRefreshGroups={loadGroups}
              onSendFeedback={(ok) =>
                setSnack({
                  open: true,
                  message: ok ? 'Отправлено в Telegram!' : 'Ошибка отправки',
                  severity: ok ? 'success' : 'error',
                })
              }
              template={templates.homework}
              onSaveTemplate={handleSaveTemplate}
            />
          )}
          {tab === 1 && (
            <WeeklyTab
              groups={groups}
              onGenerated={refresh}
              onSendFeedback={(ok) =>
                setSnack({
                  open: true,
                  message: ok ? 'Отправлено в Telegram!' : 'Ошибка отправки',
                  severity: ok ? 'success' : 'error',
                })
              }
              template={templates.weeklySummary}
              onSaveTemplate={handleSaveTemplate}
            />
          )}
          {tab === 2 && (
            <MonthlyReportTab
              groups={groups}
              onGenerated={refresh}
              onNotify={(message, severity = 'success') =>
                setSnack({ open: true, message, severity })
              }
            />
          )}
          {tab === 3 && (
            <HistoryTab
              refreshKey={historyKey}
              onSendFeedback={(ok) =>
                setSnack({
                  open: true,
                  message: ok ? 'Отправлено в Telegram!' : 'Ошибка отправки',
                  severity: ok ? 'success' : 'error',
                })
              }
            />
          )}
        </Box>
      </Paper>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
